// behaviorAnalytics.ts — CORE-497 (parent CORE-483)
//
// Typed catalog of user-behaviour events for the personalization data pipeline.
// This is a thin, typed layer over `analytics.track(...)` (→ the existing
// `AnalyticsEvents` store) — NOT a new stream. It exists so the whole app emits
// one documented, stable taxonomy that the BE interest-profile aggregation
// (UserInterestProfile) and, later, AI-personalization can read without having
// to reverse-engineer ad-hoc event strings.
//
// Scope (per the ticket AC): ACCUMULATION ONLY. No recommendations, no sorting,
// no UI logic here — this module just names events and forwards them.
//
// Adding a new signal = add a constant here + one `trackBehavior(...)` call at
// the surface. The BE aggregates from `AnalyticsEvents.Properties` (jsonb), so
// new event types need no migration — that's the "extensible without reworking
// existing logic" requirement.

import { analytics } from './analytics';

/**
 * Canonical event-name catalog. Grouped by surface. Values are the snake_case
 * strings persisted to `AnalyticsEvents.EventName`; keep them stable — the BE
 * aggregation and any dashboards key off these exact strings.
 */
export const BehaviorEvents = {
  onboarding: {
    started: 'onboarding_started',
    stepViewed: 'onboarding_step_viewed',
    stepSkipped: 'onboarding_step_skipped',
    completed: 'onboarding_completed',
    skipped: 'onboarding_skipped',
    closed: 'onboarding_closed',
    restarted: 'onboarding_restarted',
  },
  home: {
    blockViewed: 'home_block_viewed',
    itemClicked: 'home_item_clicked',
    firstAction: 'home_first_action',
  },
  master: {
    cardViewed: 'master_card_viewed',
    opened: 'master_opened',
    subscribed: 'master_subscribed',
    materialViewed: 'master_material_viewed',
  },
  practice: {
    opened: 'practice_opened',
    started: 'practice_started',
    completed: 'practice_completed',
    favorited: 'practice_favorited',
    repeated: 'practice_repeated',
  },
  event: {
    viewed: 'event_viewed',
    opened: 'event_opened',
    joined: 'event_joined',
    addedToCalendar: 'event_added_to_calendar',
  },
  agapella: {
    chatOpened: 'agapella_chat_opened',
    firstMessage: 'agapella_first_message',
    topic: 'agapella_topic',
    repeat: 'agapella_repeat',
    onboardingStarted: 'agapella_onboarding_started',
  },
  // CORE-503 — a direction filter the user explicitly picks is a
  // «выбранное направление» signal (section views are already recorded by the
  // existing *_page_viewed / *_tab_switched analytics events).
  discovery: {
    directionSelected: 'direction_selected',
  },
} as const;

/**
 * Interest signal — a structured hint the BE folds straight into the interest
 * profile (directions / preferred masters / preferred formats / activity)
 * without re-deriving it from the raw event name. Attach via the `interest`
 * prop below; it lands in `AnalyticsEvents.Properties.interest`.
 */
export type InterestSignal =
  | { kind: 'direction'; value: string }               // practice category / master direction
  | { kind: 'master'; masterId: number | string }      // affinity toward a specific master
  | { kind: 'format'; value: 'video' | 'audio' | 'live' | 'event' | 'room' }
  | { kind: 'activity'; value: string };               // generic engagement signal

export type BehaviorProps = Record<string, unknown> & {
  /**
   * One or more structured interest signals for this event. The BE aggregation
   * normalises to an array, so a single action can contribute several signals
   * at once (e.g. opening a practice = a `format` signal + a `master` signal).
   */
  interest?: InterestSignal | InterestSignal[];
};

/**
 * Emit a behaviour event. Forwards to `analytics.track` (batched + flushed to
 * `POST /api/analytics/events`); the BE stamps the authoritative `UserId` from
 * the JWT, while the client `sessionId` (already carried by analytics.ts)
 * stitches pre-login (guest) onboarding events to the account once it exists.
 */
export function trackBehavior(event: string, props?: BehaviorProps): void {
  analytics.track(event, props as Record<string, any> | undefined);
}

// CORE-503 — «первые действия пользователя». Fire `home_first_action` at most
// once per app session, from whichever meaningful action the user takes first.
// Session-scoped module flag (analytics.ts already carries a per-session id, so
// the BE can attribute the single event to the session's first move).
let firstActionFired = false;
export function trackFirstAction(props?: BehaviorProps): void {
  if (firstActionFired) return;
  firstActionFired = true;
  trackBehavior(BehaviorEvents.home.firstAction, props);
}

export type InterestFormat = 'video' | 'audio' | 'live' | 'event' | 'room';

/** Convenience helpers for the interest signals the aggregation cares about. */
export const interest = {
  direction: (value: string): InterestSignal => ({ kind: 'direction', value }),
  master: (masterId: number | string): InterestSignal => ({ kind: 'master', masterId }),
  format: (value: InterestFormat): InterestSignal => ({ kind: 'format', value }),
  activity: (value: string): InterestSignal => ({ kind: 'activity', value }),
};
